import { message } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GaEventType } from 'src/schema/schema-ga-event';
import { MobileAlert, formatNumber } from 'src/utils/common';
import { kakaoTalkUrl } from 'src/utils/constants/service-constants';
import { safeBottomForFooterButton } from 'src/utils/constants/ui-constants';
import { sendGaEvent } from 'src/utils/firebase/firebase-short-cut';
import {
  checkIsDormantCustomerReorder,
  checkIsFirstOrder,
  createOrderData,
  getDeliveryFee,
  handleCreateOrder,
  handleUpdateOrder,
  onCancelOrder,
  validateOrderConditions,
  validateOrderCreatable,
  validateOrderEditable,
} from 'src/utils/order-util';

import useCart from 'src/redux/hooks/useCart';
import useOrder from 'src/redux/hooks/useOrder';
import { selectStore, selectUser } from 'src/redux/slices/auth';
import { selectCart } from 'src/redux/slices/cart';
import { selectOnEditOrder, selectSubmiited } from 'src/redux/slices/order';
import { useAppSelector } from 'src/redux/store';

import { useApp } from 'src/stores/app-context';

import ChatIcon from 'src/assets/icons/chat.svg';

import FooterButton from '../../atoms/Button/FooterButton';
import classes from './OrderButtons.module.scss';

const openKakaoChat = () => window.open(kakaoTalkUrl, '_blank');

type OrderButtonsProps = {
  goToCart?: boolean;
  stickToBottom?: boolean;
};

const PromotiveMsg: FC<{ text: string }> = ({ text }) => {
  return (
    <div className={classes.promotiveMsg}>
      <span>{text}</span>
    </div>
  );
};

const OrderButtons: FC<OrderButtonsProps> = ({ goToCart, stickToBottom }) => {
  const navigate = useNavigate();
  const { onResetCart } = useCart();
  const user = useAppSelector(selectUser);
  const store = useAppSelector(selectStore);
  const onEditOrder = useAppSelector(selectOnEditOrder);
  const submittedOrder = useAppSelector(selectSubmiited);
  const { onSetOnEditOrder } = useOrder();
  const { showLoading, setShowLoading, commerceConf } = useApp();
  const { closed, open } = commerceConf;
  const msgClosed = useMemo(
    () => (closed.isClosed ? closed.msgClosed : open ? undefined : '주문 가능한 시간이 아닙니다.'),
    [closed, open]
  );

  const cart = useAppSelector(selectCart);
  // 배송비 무료 적용까지 남은 금액
  const leftUntilFreeDelivery = useMemo(
    () => (store?.chargeDeliveryFee ? commerceConf.minAmountForFreeDelivery - cart.totalAmount : 0),
    [cart.totalAmount, commerceConf.minAmountForFreeDelivery, store?.chargeDeliveryFee]
  );
  const deliveryFee = useMemo(
    () => getDeliveryFee(store, commerceConf, cart.totalAmount),
    [cart.totalAmount, commerceConf, store]
  );

  /**
   * 현재 주문 내역이 없고, 장바구니에 상품이 있다면 주문 버튼을 보여준다.
   */
  const showOrderButton = useMemo(
    () => submittedOrder === null && cart.products.length > 0,
    [cart.products.length, submittedOrder]
  );
  const showUpdateOrderButton = useMemo(() => onEditOrder, [onEditOrder]);

  const KakaoLinkAlertContent = useMemo(
    () => (
      <p>
        빠른 처리를 원하시는 경우
        <br />
        <span className='openKakao' onClick={openKakaoChat}>
          <img src={ChatIcon} alt='이웃삼촌 카카오톡 링크' />
          카카오톡 채널(이웃삼촌)
        </span>
        으로 문의 바랍니다.
      </p>
    ),
    []
  );

  /**
   * 주문을 생성합니다.
   */
  const createOrder = useCallback(async () => {
    setShowLoading(true);
    try {
      const orderConditionResult = await validateOrderConditions(user, store);
      if (!orderConditionResult.isValid) {
        MobileAlert(
          orderConditionResult.error.title,
          orderConditionResult.error.customAlert === 'kakaoLink'
            ? KakaoLinkAlertContent
            : orderConditionResult.error.content
        );
        setShowLoading(false);
        return;
      }

      const { user: validUser, store: validStore, commerceConf } = orderConditionResult;

      const orderCreatableResult = validateOrderCreatable(cart);
      if (orderCreatableResult) {
        message.warning(orderCreatableResult.title);
        setShowLoading(false);
        return;
      }

      checkIsFirstOrder(validStore);
      checkIsDormantCustomerReorder(validStore);
      const order = createOrderData(validUser, validStore, cart, deliveryFee, commerceConf.deliveryDateRules);
      await handleCreateOrder(order, validStore.storeNickname ?? validStore.storeName ?? '알 수 없음');
      onResetCart('주문 완료');
      message.success('주문완료!');
      onSetOnEditOrder(false);
      setShowLoading(false);
      if (goToCart) {
        navigate('/shopping-cart');
      }
    } catch (error: any) {
      message.error(error.message ?? '주문 생성 실패');
      setShowLoading(false);
    }
  }, [
    setShowLoading,
    user,
    store,
    cart,
    deliveryFee,
    onResetCart,
    onSetOnEditOrder,
    goToCart,
    KakaoLinkAlertContent,
    navigate,
  ]);

  /**
   * 주문을 수정합니다.
   * : 주문에서 상품을 모두 제거하는 경우 주문을 취소합니다.
   */
  const updateOrder = useCallback(async () => {
    setShowLoading(true);
    try {
      if (!store) {
        throw new Error('store is not found');
      }

      const orderEditableResult = await validateOrderEditable(submittedOrder, store);
      if (!orderEditableResult.isValid) {
        message.warning(orderEditableResult.error.title);
        setShowLoading(false);
        return;
      }

      const { order: editableOrder } = orderEditableResult;

      if (cart.products.length === 0) {
        onResetCart('장바구니 비우기 & 주문 취소');
        await onCancelOrder(editableOrder, store.storeNickname ?? store.storeName ?? '알 수 없음', {
          resetCart: true,
        });
        setShowLoading(false);
        return;
      }

      const orderConditionResult = await validateOrderConditions(user, store);
      if (!orderConditionResult.isValid) {
        MobileAlert(
          orderConditionResult.error.title,
          orderConditionResult.error.customAlert === 'kakaoLink'
            ? KakaoLinkAlertContent
            : orderConditionResult.error.content
        );
        setShowLoading(false);
        return;
      }

      const { user: validUser, store: validStore, commerceConf } = orderConditionResult;

      const updateOrderData = createOrderData(validUser, validStore, cart, deliveryFee, commerceConf.deliveryDateRules);
      await handleUpdateOrder(
        editableOrder._id,
        validStore.storeNickname ?? validStore.storeName ?? '알 수 없음',
        updateOrderData
      );
      onResetCart('주문 수정 완료');
      message.success('수정완료!');
      onSetOnEditOrder(false);
      setShowLoading(false);
      if (goToCart) {
        navigate('/shopping-cart');
      }
    } catch (error: any) {
      message.error(error.message ?? '주문 수정 실패');
      setShowLoading(false);
    }
  }, [
    setShowLoading,
    user,
    store,
    cart,
    deliveryFee,
    onResetCart,
    onSetOnEditOrder,
    goToCart,
    KakaoLinkAlertContent,
    submittedOrder,
    navigate,
  ]);

  const handleOrderAction = useCallback(() => {
    if (onEditOrder) {
      updateOrder();
    } else {
      createOrder();
    }
  }, [onEditOrder, updateOrder, createOrder]);

  const cancelEditOrder = useCallback(() => {
    onSetOnEditOrder(false);
    onResetCart('주문 수정 취소');
    sendGaEvent({
      name: GaEventType.ORDER_EDIT_CANCEL,
      eventParams: {
        orderId: submittedOrder?._id ?? null,
      },
    });
  }, [onResetCart, onSetOnEditOrder, submittedOrder?._id]);

  return (
    <>
      {showOrderButton && (
        <FooterButton
          bottom={stickToBottom ? safeBottomForFooterButton : undefined}
          text={msgClosed ? msgClosed : `${formatNumber(cart.totalAmount + deliveryFee)}원 주문확정하기`}
          onClick={handleOrderAction}
          disabled={!!msgClosed || showLoading}
          child={
            leftUntilFreeDelivery > 0 && (
              <PromotiveMsg text={`${formatNumber(leftUntilFreeDelivery)}원 더 담으면 무료배송`} />
            )
          }
        />
      )}
      {showUpdateOrderButton && (
        <div
          className={classes.updateOrderButtons}
          style={stickToBottom ? { bottom: safeBottomForFooterButton } : undefined}>
          <button className={classes.cancel} onClick={cancelEditOrder}>
            수정취소
          </button>
          <button className={classes.updateOrder} onClick={handleOrderAction} disabled={!!msgClosed || showLoading}>
            {msgClosed
              ? msgClosed
              : cart.totalAmount
              ? `${formatNumber(cart.totalAmount + deliveryFee)}원 수정완료`
              : '주문취소'}
            {!msgClosed && cart.totalAmount > 0 && leftUntilFreeDelivery > 0 && (
              <PromotiveMsg text={`${formatNumber(leftUntilFreeDelivery)}원 더 담으면 무료배송`} />
            )}
          </button>
        </div>
      )}
    </>
  );
};

export default OrderButtons;
